import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { X, Plus, Pencil, Trash2, ChevronDown, ChevronRight, GripVertical, Check, AlertTriangle } from 'lucide-react'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'
import { requestCreator } from '../../../utilities/requests'
import toast from '../../../utilities/toast'
import SkillsEditor from './SkillsEditor'

const { get: getSkillLevels, post: createSkillLevel, put: updateSkillLevel, delete: deleteSkillLevel, cancel: cancelRequests } = requestCreator()

function SkillLevelDrawer({ open, onOpenChange, curriculum, facilityId, readOnly = false, onSkillLevelsUpdated }) {
  const [skillLevels, setSkillLevels] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedLevelId, setExpandedLevelId] = useState(null)
  const [editingLevelId, setEditingLevelId] = useState(null)
  const [deletingLevelId, setDeletingLevelId] = useState(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({ name: '', minAge: '', maxAge: '' })
  const [editingSkills, setEditingSkills] = useState([])

  const loadSkillLevels = useCallback(async () => {
    if (!curriculum?.id) return

    setIsLoading(true)
    try {
      const data = await getSkillLevels(`/api/facilities/${facilityId}/skill_levels`, {
        params: { 
          curriculum_id: curriculum.id,
          include: 'skills'
        }
      })
      setSkillLevels(data)
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error('Failed to load skill levels')
      }
    } finally {
      setIsLoading(false)
    }
  }, [facilityId, curriculum?.id])

  useEffect(() => {
    if (open && curriculum) {
      loadSkillLevels()
    }
    return cancelRequests
  }, [open, curriculum, loadSkillLevels])

  function handleToggleExpand(levelId) {
    setExpandedLevelId(expandedLevelId === levelId ? null : levelId)
  }

  function handleStartEdit(level) {
    setEditingLevelId(level.id)
    setFormData({ name: level.name, minAge: level.minAge ?? '', maxAge: level.maxAge ?? '' })
    setEditingSkills(level.skills || [])
    setExpandedLevelId(level.id)
  }

  function handleStartDelete(level) {
    setDeletingLevelId(level.id)
  }

  function handleCancelEdit() {
    setEditingLevelId(null)
    setDeletingLevelId(null)
    setIsAddingNew(false)
    setFormData({ name: '', minAge: '', maxAge: '' })
    setEditingSkills([])
  }

  function handleStartAdd() {
    setIsAddingNew(true)
    setFormData({ name: '', minAge: '', maxAge: '' })
    setEditingSkills([])
  }

  async function handleSaveNew() {
    if (!formData.name.trim()) {
      toast.error('Please enter a skill level name')
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        skill_level: {
          name: formData.name,
          min_age: formData.minAge !== '' ? Number(formData.minAge) : null,
          max_age: formData.maxAge !== '' ? Number(formData.maxAge) : null,
        },
        curriculum_id: curriculum.id,
        skills: editingSkills.map((skill, index) => ({
          name: skill.name,
          short_label: skill.shortLabel || String(index + 1),
          ordinal: index + 1,
          optional: skill.optional || false
        }))
      }

      await createSkillLevel(`/api/facilities/${facilityId}/skill_levels`, payload)
      toast.success('Skill level created successfully')
      handleCancelEdit()
      loadSkillLevels()
      onSkillLevelsUpdated?.()
    } catch (error) {
      toast.error('Failed to create skill level')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleSaveEdit() {
    if (!formData.name.trim()) {
      toast.error('Please enter a skill level name')
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        skill_level: {
          name: formData.name,
          min_age: formData.minAge !== '' ? Number(formData.minAge) : null,
          max_age: formData.maxAge !== '' ? Number(formData.maxAge) : null,
        },
        skills: editingSkills.map((skill, index) => ({
          id: skill.id || undefined,
          name: skill.name,
          short_label: skill.shortLabel || String(index + 1),
          ordinal: index + 1,
          optional: skill.optional || false
        }))
      }

      await updateSkillLevel(`/api/facilities/${facilityId}/skill_levels/${editingLevelId}`, payload)
      toast.success('Skill level updated successfully')
      handleCancelEdit()
      loadSkillLevels()
      onSkillLevelsUpdated?.()
    } catch (error) {
      toast.error('Failed to update skill level')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleConfirmDelete() {
    setIsSaving(true)
    try {
      await deleteSkillLevel(`/api/facilities/${facilityId}/skill_levels/${deletingLevelId}`)
      toast.success('Skill level deleted successfully')
      handleCancelEdit()
      loadSkillLevels()
      onSkillLevelsUpdated?.()
    } catch (error) {
      toast.error('Failed to delete skill level. It may be in use.')
    } finally {
      setIsSaving(false)
    }
  }

  function renderSkillLevelItem(level) {
    const isExpanded = expandedLevelId === level.id
    const isEditing = editingLevelId === level.id
    const isDeleting = deletingLevelId === level.id
    const skillCount = level.skills?.length || 0

    return (
      <div key={level.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div 
          className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50"
          onClick={() => !isEditing && !isDeleting && handleToggleExpand(level.id)}
        >
          <button type="button" className="mr-2 text-gray-400">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-900">{level.name}</span>
            <span className="ml-2 text-xs text-gray-500">({skillCount} skills)</span>
          </div>
          {!readOnly && !isEditing && !isDeleting && (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleStartEdit(level)}
                className="h-8 w-8 p-0 text-gray-500 hover:text-purple-600"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleStartDelete(level)}
                className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {isDeleting && (
          <div className="px-4 py-3 bg-red-50 border-t border-red-100">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Delete &quot;{level.name}&quot;?</p>
                <p className="text-sm text-red-600 mt-1">
                  This will also delete all {skillCount} skills in this level. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleConfirmDelete}
                disabled={isSaving}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {isSaving ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="px-4 py-3 bg-purple-50 border-t border-purple-100">
            <div className="space-y-4">
              <div>
                <Label htmlFor={`edit-level-${level.id}`} className="block mb-1 text-sm font-medium">
                  Level Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id={`edit-level-${level.id}`}
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white"
                  placeholder="e.g., Basic 1"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`edit-min-age-${level.id}`} className="block mb-1 text-sm font-medium">
                    Min Age <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id={`edit-min-age-${level.id}`}
                    type="number"
                    min="0"
                    value={formData.minAge}
                    onChange={(e) => setFormData({ ...formData, minAge: e.target.value })}
                    className="w-full bg-white"
                    placeholder="e.g., 6"
                  />
                </div>
                <div>
                  <Label htmlFor={`edit-max-age-${level.id}`} className="block mb-1 text-sm font-medium">
                    Max Age <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id={`edit-max-age-${level.id}`}
                    type="number"
                    min="0"
                    value={formData.maxAge}
                    onChange={(e) => setFormData({ ...formData, maxAge: e.target.value })}
                    className="w-full bg-white"
                    placeholder="e.g., 18"
                  />
                </div>
              </div>

              <SkillsEditor
                skills={editingSkills}
                onChange={setEditingSkills}
                readOnly={false}
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button type="button" size="sm" onClick={handleSaveEdit} disabled={isSaving} className="bg-purple-600 hover:bg-purple-700">
                  <Check className="h-4 w-4 mr-1" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {isExpanded && !isEditing && !isDeleting && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            {skillCount === 0 ? (
              <p className="text-sm text-gray-500 italic">No skills defined for this level</p>
            ) : (
              <div className="space-y-1">
                {level.skills.map((skill, index) => (
                  <div key={skill.id} className="flex items-center gap-2 py-1 text-sm">
                    <span className="w-6 h-6 flex items-center justify-center bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      {skill.shortLabel || index + 1}
                    </span>
                    <span className="text-gray-700">{skill.name}</span>
                    {skill.optional && (
                      <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Optional</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  function renderNewLevelForm() {
    if (!isAddingNew) return null

    return (
      <div className="border border-purple-200 rounded-lg overflow-hidden bg-white mb-4">
        <div className="px-4 py-3 bg-purple-100 border-b border-purple-200">
          <h3 className="text-sm font-semibold text-purple-900">New Skill Level</h3>
        </div>
        <div className="px-4 py-3 space-y-4">
          <div>
            <Label htmlFor="new-level-name" className="block mb-1 text-sm font-medium">
              Level Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-level-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full"
              placeholder="e.g., Basic 1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="new-min-age" className="block mb-1 text-sm font-medium">
                Min Age <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="new-min-age"
                type="number"
                min="0"
                value={formData.minAge}
                onChange={(e) => setFormData({ ...formData, minAge: e.target.value })}
                className="w-full"
                placeholder="e.g., 6"
              />
            </div>
            <div>
              <Label htmlFor="new-max-age" className="block mb-1 text-sm font-medium">
                Max Age <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="new-max-age"
                type="number"
                min="0"
                value={formData.maxAge}
                onChange={(e) => setFormData({ ...formData, maxAge: e.target.value })}
                className="w-full"
                placeholder="e.g., 18"
              />
            </div>
          </div>

          <SkillsEditor
            skills={editingSkills}
            onChange={setEditingSkills}
            readOnly={false}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSaveNew} disabled={isSaving} className="bg-purple-600 hover:bg-purple-700">
              <Check className="h-4 w-4 mr-1" />
              {isSaving ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const drawerTitle = curriculum ? `${curriculum.name} - Skill Levels` : 'Skill Levels'

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col md:!w-[600px] md:!min-w-[600px] md:!max-w-[600px]">
        <DrawerHeader className="border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle>{drawerTitle}</DrawerTitle>
              {readOnly && (
                <p className="text-xs text-gray-500 mt-1">This is a shared curriculum (read-only)</p>
              )}
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="sm">
                <X className="w-4 h-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              {!readOnly && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleStartAdd}
                    disabled={isAddingNew}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Skill Level
                  </Button>
                </div>
              )}

              {renderNewLevelForm()}

              {skillLevels.length === 0 && !isAddingNew ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-gray-500">No skill levels defined for this curriculum</p>
                  {!readOnly && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleStartAdd}
                      className="mt-4 bg-purple-600 hover:bg-purple-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add First Skill Level
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {skillLevels.map(renderSkillLevelItem)}
                </div>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

SkillLevelDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  curriculum: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    isFacilityOwned: PropTypes.bool
  }),
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  readOnly: PropTypes.bool,
  onSkillLevelsUpdated: PropTypes.func
}

export default SkillLevelDrawer
